import type { IEnvironment } from 'relay-runtime';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';
import { fireIAErrorAnalytics } from '@atlassian/jira-ui-modifications-analytics';
import {
	getSupportedFieldConfiguration,
	FieldValueLookupError,
} from '@atlassian/jira-ui-modifications-fields-configuration';

import type {
	OptionsVisibilityValidator,
	PropertyTypeValidator,
	SupportedFieldProperty,
} from '@atlassian/jira-ui-modifications-fields-configuration/src/common/utils/common/types';
import type { FieldInternalMetadata } from '@atlassian/jira-ui-modifications-fields-configuration/src/common/utils/common/types/field-internal-metadata';
import type {
	LookupData,
	FieldPublicShape,
	FieldId,
	FieldType,
	FieldInternalShape,
	Field,
} from '@atlassian/jira-ui-modifications-fields-configuration/src/types';
import type { ViewType, ViewContext, TenantContext } from '@atlassian/jira-ui-modifications-types';

import { IssueAdjustmentFieldPropertyError } from '../errors';

type ValidateFieldFromIframeArgs = {
	fieldType: FieldType;
	fieldId: FieldId;
	fieldFromIframe: FieldPublicShape;
	viewType: ViewType;
	fieldMetadata: FieldInternalMetadata;
	currentField: Field;
	createAnalyticsEvent: CreateUIAnalyticsEvent;
};

// TODO: remove validateFieldFromIframe with forge-ui-modifications-validation-errors-returned-to-app_hdzmw clean up
export const validateFieldFromIframe = async ({
	fieldType,
	fieldId,
	currentField,
	fieldFromIframe,
	fieldMetadata,
	viewType,
	createAnalyticsEvent,
}: ValidateFieldFromIframeArgs): Promise<void> => {
	const fieldConfiguration = getSupportedFieldConfiguration(viewType, fieldType);
	const fieldValidators = fieldConfiguration?.publicShapeValidators;

	if (!fieldFromIframe || !fieldValidators) {
		return;
	}

	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const validatorsArray = Object.entries<PropertyTypeValidator | OptionsVisibilityValidator>(
		fieldValidators,
	) as [SupportedFieldProperty, PropertyTypeValidator | OptionsVisibilityValidator][];

	await Promise.all(
		validatorsArray.map(async ([propertyName, validate]) => {
			if (Object.prototype.hasOwnProperty.call(fieldFromIframe, propertyName)) {
				const propertyValue = fieldFromIframe[propertyName];
				if (
					(await validate(propertyValue, fieldMetadata, createAnalyticsEvent, {
						currentField,
						viewType,
					})) === false
				) {
					throw new IssueAdjustmentFieldPropertyError(
						`UIM tried to set an invalid value for "${propertyName}" property on a field (fieldId: "${fieldId}", fieldType: "${fieldType}")`,
						{ fieldId, fieldType },
					);
				}
			}
		}),
	);
};

export const transformFieldToIframe = (
	fieldChanges: FieldInternalShape,
	viewType: ViewType,
	fieldType: FieldType,
	lookupData: LookupData,
	fieldFromIframe: FieldPublicShape,
	fieldMetadata: FieldInternalMetadata,
): FieldPublicShape => {
	const fieldConfiguration = getSupportedFieldConfiguration(viewType, fieldType);
	const fieldInternalToPublicTransformers = fieldConfiguration?.internalToPublicTransformers;

	const publicShapeField: FieldPublicShape = { ...fieldChanges };

	if (publicShapeField.value !== undefined && fieldInternalToPublicTransformers?.value) {
		try {
			publicShapeField.value = fieldInternalToPublicTransformers?.value(
				fieldChanges.value,
				fieldMetadata.fieldRenderer,
			);
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (err: any) {
			fireIAErrorAnalytics({
				error: err,
				id: 'internalToPublicValueTransformer',
				viewType,
				attributes: {
					fieldType,
				},
			});
		}
	}

	if (
		fieldFromIframe.optionsVisibility !== undefined &&
		fieldInternalToPublicTransformers?.optionsVisibility
	) {
		try {
			const lookupSource = fieldConfiguration?.lookupSource;
			const lookupValues = lookupSource ? lookupData[lookupSource] : undefined;

			publicShapeField.optionsVisibility = fieldInternalToPublicTransformers?.optionsVisibility(
				fieldFromIframe.optionsVisibility,
				lookupValues,
			);
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (err: any) {
			fireIAErrorAnalytics({
				error: err,
				id: 'internalToPublicOptionsVisibilityTransformer',
				viewType,
				attributes: {
					fieldType,
				},
			});
		}
	}

	return publicShapeField;
};

// TODO: remove transformFieldFromIframe with forge-ui-modifications-validation-errors-returned-to-app_hdzmw clean up
export const transformFieldFromIframe = async ({
	fieldType,
	fieldId,
	fieldFromIframe,
	lookupData,
	viewType,
	tenantContext,
	viewContext,
	environment,
}: {
	fieldType: FieldType;
	fieldId: FieldId;
	fieldFromIframe: FieldPublicShape;
	lookupData: LookupData;
	viewType: ViewType;
	tenantContext: TenantContext;
	viewContext: ViewContext;
	environment: IEnvironment;
}): Promise<FieldInternalShape> => {
	const fieldConfiguration = getSupportedFieldConfiguration(viewType, fieldType);
	const fieldTransformers = fieldConfiguration?.publicToInternalTransformers;
	const fieldValidators = fieldConfiguration?.publicShapeValidators;

	if (!fieldFromIframe || !fieldTransformers || !fieldValidators) {
		return {};
	}

	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const validatorsArray = Object.keys(fieldValidators) as SupportedFieldProperty[];
	const lookupSource = fieldConfiguration?.lookupSource;
	const lookupValues = lookupSource ? lookupData[lookupSource] : undefined;
	return validatorsArray.reduce(async (acc: Promise<FieldPublicShape>, propertyName) => {
		if (Object.prototype.hasOwnProperty.call(fieldFromIframe, propertyName)) {
			const propertyValue = fieldFromIframe[propertyName];
			const transformer = fieldTransformers[propertyName];
			const fieldContext = { fieldId };

			// We need to catch lookup values discrepancies and react accordingly
			// to how we react to validation errors - throw an error
			// if the set value wasn't found in the array of lookupValues
			try {
				(await acc)[propertyName] = transformer
					? // eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
						await transformer(propertyValue, lookupValues as any, {
							tenantContext,
							viewContext,
							environment,
							fieldContext,
						})
					: propertyValue;
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (err: any) {
				// FieldValueLookupError will be thrown from the transformer
				// when the lookup value wasn't found
				if (err instanceof FieldValueLookupError) {
					// For single value fields its straightforward to just pass the faulty propertyValue
					// into IssueAdjustmentFieldPropertyError error message
					// but for multi value fields we should find out the faulty values from the array
					// thats why we should propagate the faulty values as a comma separated string
					// by using the error's message
					const faultyValue = err.message || propertyValue;

					throw new IssueAdjustmentFieldPropertyError(
						`Field with ID "${fieldId}" has invalid values set in "${propertyName}": ${faultyValue}`,
						{ fieldId, fieldType },
					);
				}

				throw err;
			}
		}

		return acc;
	}, Promise.resolve({}));
};
